import React, { useEffect } from 'react';

import { useGroupState, useGroupActions } from './GroupContext';
import { useGroupSettingsCapabilities } from './useGroupSettingsCapabilities';
import { useCapabilities } from '../../Shared/capabilities';
import GroupSettingsLoader from './GroupSettingsLoader';
import DeleteGroupModal from './DeleteGroupModal';
import PermissionsSection from './PermissionsSection';
import GroupAdminsSection from './GroupAdminsSection';
import GroupMembersSection from './GroupMembersSection';
import Sidebar from '../../Shared/UI/Sidebar/components/Sidebar';
import SidebarItem from '../../Shared/UI/Sidebar/components/SidebarItem';
import Button from '../../Shared/UI/Button/components/Button';
import FeaturesSection from './FeaturesSection';
import GroupBillingSection from './GroupBillingSection';
import FEATURE_SIDEBAR_ITEMS from './featureSidebarItems';

import styles from './GroupSettings.module.scss';

const GroupSettings = ({ id, stripePublishableKey }) => {
  const state = useGroupState();
  const { initializeState, openDeleteGroupModal } = useGroupActions();

  useEffect(() => {
    initializeState(id, { stripePublishableKey });
  }, []);

  useGroupSettingsCapabilities();
  const capabilities = useCapabilities();

  const groupPath = state?.group?.path || `/groups/${id}`;

  return (
    <>
      <GroupSettingsLoader />
      <DeleteGroupModal />
      <section id={styles.settingsContainer}>
        <div id={styles.settingsBody}>
          <Sidebar aria-label="Group navigation">
            <SidebarItem href={groupPath}>Overview</SidebarItem>
            <SidebarItem href={`${groupPath}/members`}>Members</SidebarItem>
            {state?.group?.enabledFeatures?.map(feature => {
              const config = FEATURE_SIDEBAR_ITEMS[feature];
              if (!config) return null;
              if (config.capability && !capabilities[config.capability]) return null;
              return (
                <SidebarItem key={feature} href={`${groupPath}${config.path}`}>
                  {config.label}
                </SidebarItem>
              );
            })}
            {capabilities.canManageInvitations && (
              <SidebarItem href={`${groupPath}/invitations`}>Invitations</SidebarItem>
            )}
            <SidebarItem href={`${groupPath}/settings`} active>Settings</SidebarItem>
          </Sidebar>
          <div id={styles.settingsContent}>
            <h1 className={styles.pageTitle}>Settings</h1>
            <FeaturesSection />
            <GroupBillingSection />
            <PermissionsSection />
            <GroupAdminsSection />
            <GroupMembersSection />
            {capabilities.canDeleteGroup && (
              <div className={styles.dangerZone}>
                <h3 className={styles.dangerZoneTitle}>Danger Zone</h3>
                <div className={styles.dangerZoneAction}>
                  <div>
                    <strong>Delete this group</strong>
                    <p>Once deleted, this group and all of its associated data cannot be recovered.</p>
                  </div>
                  <Button variant="destructive" onClick={() => openDeleteGroupModal()}>
                    Delete Group
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default GroupSettings;
